import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../../utils/authUtils';
import { Review } from '../../types/ReviewTypes';
import ReviewFormInline from '../Review/ReviewFormInline';
import CreatePlaylistModal from '../Playlist/CreatePlaylistModal';
import './EllipsisDropdown.css';

interface EllipsisDropdownProps {
    onReviewSong?: () => void;
    onAddToPlaylist?: () => void;
    onOpenInSpotify?: () => void;
    spotifySongId?: string;
    className?: string;
    songId?: number;
    songName?: string;
}

interface Playlist {
    _id: number;
    name: string;
    user_id: number;
}

interface ToastMessage {
    id: string;
    message: string;
    type: 'success' | 'error';
}

const EllipsisDropdown: React.FC<EllipsisDropdownProps> = ({
    onReviewSong,
    onAddToPlaylist,
    onOpenInSpotify,
    spotifySongId,
    className = '',
    songId,
    songName
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showPlaylistMenu, setShowPlaylistMenu] = useState(false);
    const [showCreatePlaylistModal, setShowCreatePlaylistModal] = useState(false);
    const [playlists, setPlaylists] = useState<Playlist[]>([]);
    const [isLoadingPlaylists, setIsLoadingPlaylists] = useState(false);
    const [existingReview, setExistingReview] = useState<Review | null>(null);
    const [isLoadingReview, setIsLoadingReview] = useState(false);
    const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const playlistMenuRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
                dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                playlistMenuRef.current && !playlistMenuRef.current.contains(event.target as Node)) {
                closeAllDropdowns();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Recalculate dropdown position on scroll
    useEffect(() => {
        const handleScroll = () => {
            if (isOpen || showPlaylistMenu) {
                recalculateDropdownPosition();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isOpen, showPlaylistMenu]);

    // Auto-remove toast messages after 2.5 seconds
    useEffect(() => {
        if (toastMessages.length === 0) return;

        const timer = setTimeout(() => {
            setToastMessages(prev => prev.slice(1));
        }, 2500);

        return () => clearTimeout(timer);
    }, [toastMessages]);

    const showToast = (message: string, type: 'success' | 'error') => {
        const id = Date.now().toString();
        setToastMessages(prev => [...prev, { id, message, type }]);
    };

    const closeAllDropdowns = () => {
        setIsOpen(false);
        setShowPlaylistMenu(false);
        setShowReviewModal(false);
        setShowCreatePlaylistModal(false);
    };

    const recalculateDropdownPosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 4, // 4px margin
                left: rect.right - 160 // Align to right edge, accounting for dropdown width
            });
        }
    };

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 4, // 4px margin
                left: rect.right - 160 // Align to right edge, accounting for dropdown width
            });
        }

        setIsOpen(!isOpen);
        setShowPlaylistMenu(false);
    };

    const handleOptionClick = (action: (() => void) | undefined) => {
        setIsOpen(false);
        if (action) {
            action();
        }
    };

    const fetchExistingReview = async (songId: number): Promise<Review | null> => {
        try {
            const response = await fetch(`/api/reviews/song/${songId}`, {
                credentials: 'include'
            });

            if (response.ok) {
                const reviews = await response.json();

                // Get user data from local storage
                const userData = localStorage.getItem('user');
                if (!userData) return null;

                const user = JSON.parse(userData);
                const userId = user.userId || user.id || user._id;

                // Find the user's review
                const userReview = reviews.find((review: any) =>
                    review.user_id === userId || review.is_owner === true
                );

                return userReview || null;
            }
        } catch (error) {
            console.error("Error fetching existing review:", error);
        }

        return null;
    };

    const handleReviewSong = async () => {
        if (!isAuthenticated()) {
            // Store current location and redirect to login
            localStorage.setItem('returnToUrl', window.location.pathname + window.location.search);
            navigate('/login');
            return;
        }

        if (songId) {
            setIsLoadingReview(true);
            const review = await fetchExistingReview(songId);
            setExistingReview(review);
            setIsLoadingReview(false);
        }

        setShowReviewModal(true);
        setIsOpen(false);
    };

    const handleAddToPlaylist = async () => {
        if (!isAuthenticated()) {
            // Store current location and redirect to login
            localStorage.setItem('returnToUrl', window.location.pathname + window.location.search);
            navigate('/login');
            return;
        }

        // Close the original dropdown first
        setIsOpen(false);

        setIsLoadingPlaylists(true);
        try {
            // Fetch playlists for the current user using the correct endpoint
            const response = await fetch(`/api/playlists/mine`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();

                // Ensure data is an array
                if (Array.isArray(data)) {
                    setPlaylists(data);
                } else if (data && Array.isArray(data.playlists)) {
                    setPlaylists(data.playlists);
                } else if (data && Array.isArray(data.data)) {
                    setPlaylists(data.data);
                } else {
                    console.error('Unexpected user playlists response format:', data);
                    setPlaylists([]);
                }
                setShowPlaylistMenu(true);
            } else {
                console.error('Failed to fetch user playlists:', response.status, response.statusText);
                setPlaylists([]);
            }
        } catch (error) {
            console.error('Error fetching user playlists:', error);
            setPlaylists([]);
        } finally {
            setIsLoadingPlaylists(false);
        }
    };

    const handleSpotifyClick = () => {
        if (spotifySongId) {
            window.open(`https://open.spotify.com/track/${spotifySongId}`, '_blank');
        }
        closeAllDropdowns();
    };

    const handleNewPlaylist = () => {
        setShowCreatePlaylistModal(true);
        setShowPlaylistMenu(false);
    };

    const handlePlaylistSelect = async (playlistId: number, playlistName: string) => {
        if (!songId) {
            console.error('No song ID available');
            return;
        }

        try {
            const response = await fetch(`/api/playlists/${playlistId}/songs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    song_id: songId
                })
            });

            const data = await response.json();

            if (response.ok && data.message === "Song already exists in this playlist") {
                // Song already exists in playlist
                showToast(`song already in ${playlistName}`, 'error');
            } else if (response.ok) {
                // Success - song was added
                showToast(`added to ${playlistName}`, 'success');
            } else {
                // Other error
                console.error('Error adding song to playlist:', data);
                showToast('failed to add song to playlist', 'error');
            }
        } catch (error) {
            console.error('Error adding song to playlist:', error);
            showToast('failed to add song to playlist', 'error');
        }

        closeAllDropdowns();
    };

    const handleReviewAdded = (review: any) => {
        closeAllDropdowns();
        setExistingReview(review);
        // Notify other components that a review was added for this specific song
        window.dispatchEvent(new CustomEvent('reviewUpdated', {
            detail: { songId, review, action: 'added' }
        }));
    };

    const handleReviewUpdated = (review: any) => {
        closeAllDropdowns();
        setExistingReview(review);
        // Notify other components that a review was updated for this specific song
        window.dispatchEvent(new CustomEvent('reviewUpdated', {
            detail: { songId, review, action: 'updated' }
        }));
    };

    const handleReviewCancel = () => {
        closeAllDropdowns();
        setExistingReview(null);
    };

    return (
        <>
            <div className={`ellipsis-dropdown ${className}`} onClick={(e) => e.stopPropagation()}>
                <button
                    ref={buttonRef}
                    className="ellipsis-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggle(e);
                    }}
                    aria-label="More actions"
                >
                    <span className="ellipsis-dots">⋮</span>
                </button>
            </div>

            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="dropdown-menu-portal"
                    style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        zIndex: 10000
                    }}
                >
                    <button
                        className="dropdown-item"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReviewSong();
                        }}
                    >
                        review song
                    </button>
                    <button
                        className="dropdown-item"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleAddToPlaylist();
                        }}
                    >
                        add song to playlist
                    </button>
                    {spotifySongId && (
                        <button
                            className="dropdown-item"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSpotifyClick();
                            }}
                        >
                            open in spotify
                        </button>
                    )}
                </div>,
                document.body
            )}

            {showPlaylistMenu && createPortal(
                <div
                    ref={playlistMenuRef}
                    className="dropdown-menu-portal"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        zIndex: 10000
                    }}
                >
                    <button
                        className="dropdown-item"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleNewPlaylist();
                        }}
                    >
                        + new playlist
                    </button>
                    {isLoadingPlaylists ? (
                        <div className="dropdown-item disabled">Loading playlists...</div>
                    ) : Array.isArray(playlists) && playlists.length > 0 ? (
                        playlists.map((playlist) => (
                            <button
                                key={playlist._id}
                                className="dropdown-item"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlaylistSelect(playlist._id, playlist.name);
                                }}
                            >
                                {playlist.name}
                            </button>
                        ))
                    ) : (
                        <div className="dropdown-item disabled">No playlists found</div>
                    )}
                </div>,
                document.body
            )}

            {showReviewModal && createPortal(
                <div
                    className="modal-overlay"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10001
                    }}
                >
                    <div
                        className="modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            padding: '2rem',
                            borderRadius: '8px',
                            maxWidth: '500px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h3>Review: {songName}</h3>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleReviewCancel();
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    padding: '0.5rem'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        {isLoadingReview ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                Loading review...
                            </div>
                        ) : (
                            <ReviewFormInline
                                songId={songId}
                                itemId={songId}
                                itemType="song"
                                existingReview={existingReview}
                                onReviewAdded={handleReviewAdded}
                                onReviewUpdated={handleReviewUpdated}
                                onCancel={handleReviewCancel}
                            />
                        )}
                    </div>
                </div>,
                document.body
            )}

            {showCreatePlaylistModal && createPortal(
                <CreatePlaylistModal
                    isOpen={showCreatePlaylistModal}
                    onClose={() => setShowCreatePlaylistModal(false)}
                    onPlaylistCreated={(newPlaylist) => {
                        setPlaylists(prev => [...prev, newPlaylist]);
                        if (songId) {
                            showToast(`Playlist "${newPlaylist.name}" created with song`, 'success');
                        } else {
                            showToast(`Playlist "${newPlaylist.name}" created`, 'success');
                        }
                        closeAllDropdowns();
                    }}
                    songId={songId}
                />,
                document.body
            )}

            {/* Toast Messages */}
            {toastMessages.length > 0 && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        bottom: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10002,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px'
                    }}
                >
                    {toastMessages.map((toast) => (
                        <div
                            key={toast.id}
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.65)',
                                color: '#333',
                                padding: '12px 24px',
                                borderRadius: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                fontSize: '14px',
                                fontWeight: '500',
                                minWidth: '200px',
                                textAlign: 'center',
                                backdropFilter: 'blur(10px)'
                            }}
                        >
                            {toast.message}
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

export default EllipsisDropdown; 